import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Bell, Send, Loader2, Smile, Image, X, Link2 } from 'lucide-react';
import MessageReactions from '@/components/MessageReactions';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format message time helper
const formatMessageTime = (dateString, t) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return t?.('just_now') || 'Just now';
  if (diffMins < 60) return `${diffMins}${t?.('min_ago') || 'm ago'}`;
  if (diffHours < 24) return `${diffHours}${t?.('hour_ago') || 'h ago'}`;
  if (diffDays < 7) return `${diffDays}${t?.('day_ago') || 'd ago'}`;
  return date.toLocaleDateString();
};

const ChatTab = ({ 
  communityId,
  messages, 
  currentUserId, 
  isAdmin,
  onSendMessage,
  onOpenBroadcast,
  onReactionChange
}) => {
  const { t } = useLanguage();
  const [chatInput, setChatInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [stickerPacks, setStickerPacks] = useState([]);
  const [customStickers, setCustomStickers] = useState([]);
  const [stickerPopoverOpen, setStickerPopoverOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch stickers on mount
  useEffect(() => {
    const fetchStickers = async () => {
      try {
        const [defaultRes, customRes] = await Promise.all([
          axios.get(`${API}/chat/stickers`),
          axios.get(`${API}/chat/communities/${communityId}/stickers`)
        ]);
        setStickerPacks(defaultRes.data.packs || []);
        setCustomStickers(customRes.data.custom_stickers || []);
      } catch (error) {
        console.error('Failed to fetch stickers:', error);
      }
    };
    if (communityId) fetchStickers();
  }, [communityId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() && !imagePreview) return;
    
    setSendingMessage(true);
    try {
      // If there's an image, upload it first
      if (imagePreview) {
        await axios.post(`${API}/chat/communities/${communityId}/media`, {
          media_type: 'image',
          file_name: 'shared_image.png',
          file_data: imagePreview
        });
      }
      
      // Send message (include image indicator if uploaded)
      const messageContent = imagePreview 
        ? (chatInput.trim() ? `📷 ${chatInput.trim()}` : '📷 Shared a photo')
        : chatInput.trim();
      
      await onSendMessage(messageContent);
      setChatInput('');
      setImagePreview(null);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStickerSelect = async (sticker) => {
    setStickerPopoverOpen(false);
    setSendingMessage(true);
    try {
      // Send sticker as message with emoji
      const stickerMessage = sticker.emoji || `[sticker:${sticker.name}]`;
      await onSendMessage(stickerMessage);
    } catch (error) {
      toast.error('Failed to send sticker');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImagePreview = () => {
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex flex-col h-[450px]">
      {/* Messages Container */}
      <ScrollArea className="flex-1 pr-4">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, idx) => (
              <motion.div
                key={msg.message_id || idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.user_id === currentUserId ? 'flex-row-reverse' : ''}`}
              >
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage src={msg.user_picture} />
                  <AvatarFallback>{msg.user_name?.charAt(0) || '?'}</AvatarFallback>
                </Avatar>
                <div className={`max-w-[70%] ${msg.user_id === currentUserId ? 'text-right' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">{msg.user_name}</span>
                    {msg.is_broadcast && (
                      <Badge variant="outline" className="text-xs">
                        <Bell className="w-3 h-3 mr-1" />
                        Broadcast
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatMessageTime(msg.created_at)}
                    </span>
                  </div>
                  <div className={`rounded-lg p-3 ${
                    msg.user_id === currentUserId 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  } ${msg.is_broadcast ? 'border-2 border-amber-500' : ''}`}>
                    {/* Check if message is a large emoji/sticker */}
                    {msg.content.length <= 4 && /^[\p{Emoji}]+$/u.test(msg.content) ? (
                      <span className="text-4xl">{msg.content}</span>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                    
                    {/* Link Preview */}
                    {msg.link_preview && (
                      <a 
                        href={msg.link_preview.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`block mt-2 rounded-lg overflow-hidden border ${
                          msg.user_id === currentUserId 
                            ? 'border-white/20 bg-white/10' 
                            : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800'
                        }`}
                      >
                        {msg.link_preview.image && (
                          <img 
                            src={msg.link_preview.image} 
                            alt="" 
                            className="w-full h-32 object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <div className="p-2">
                          <div className="flex items-center gap-1 text-xs opacity-60 mb-1">
                            <Link2 className="w-3 h-3" />
                            {msg.link_preview.site_name || new URL(msg.link_preview.url).hostname}
                          </div>
                          <p className="text-sm font-medium line-clamp-1">
                            {msg.link_preview.title}
                          </p>
                          {msg.link_preview.description && (
                            <p className="text-xs line-clamp-2 mt-1 opacity-70">
                              {msg.link_preview.description}
                            </p>
                          )}
                        </div>
                      </a>
                    )}
                  </div>
                  <MessageReactions
                    messageId={msg.message_id}
                    reactions={msg.reactions || {}}
                    currentUserId={currentUserId}
                    onReactionChange={(newReactions) => onReactionChange(msg.message_id, newReactions)}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </ScrollArea>
      
      {/* Image Preview */}
      <AnimatePresence>
        {imagePreview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t pt-2"
          >
            <div className="relative inline-block">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="h-20 rounded-lg object-cover"
              />
              <Button
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 w-6 h-6"
                onClick={removeImagePreview}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Chat Input with Stickers and Image */}
      <div className="border-t pt-4 mt-2">
        <form onSubmit={handleSubmit} className="flex gap-2 items-center">
          {/* Sticker Picker */}
          <Popover open={stickerPopoverOpen} onOpenChange={setStickerPopoverOpen}>
            <PopoverTrigger asChild>
              <Button 
                type="button" 
                variant="ghost" 
                size="icon"
                data-testid="sticker-btn"
              >
                <Smile className="w-5 h-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2" align="start">
              <Tabs defaultValue={stickerPacks[0]?.pack_id || 'church_pack'}>
                <TabsList className="w-full grid grid-cols-3 h-8">
                  {stickerPacks.map(pack => (
                    <TabsTrigger 
                      key={pack.pack_id} 
                      value={pack.pack_id}
                      className="text-xs"
                    >
                      {pack.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {stickerPacks.map(pack => (
                  <TabsContent key={pack.pack_id} value={pack.pack_id} className="mt-2">
                    <div className="grid grid-cols-5 gap-1">
                      {pack.stickers.map(sticker => (
                        <Button
                          key={sticker.id}
                          variant="ghost"
                          className="text-2xl p-2 h-auto hover:bg-muted"
                          onClick={() => handleStickerSelect(sticker)}
                          title={sticker.name}
                        >
                          {sticker.emoji}
                        </Button>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              {customStickers.length > 0 && (
                <div className="border-t mt-2 pt-2">
                  <p className="text-xs text-muted-foreground mb-2">Custom Stickers</p>
                  <div className="grid grid-cols-5 gap-1">
                    {customStickers.map(sticker => (
                      <Button
                        key={sticker.sticker_id}
                        variant="ghost"
                        className="p-1 h-auto"
                        onClick={() => handleStickerSelect({ name: sticker.name, emoji: `[${sticker.name}]` })}
                      >
                        <img 
                          src={sticker.image_url} 
                          alt={sticker.name}
                          className="w-8 h-8 object-contain"
                        />
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Image Upload */}
          <Button 
            type="button" 
            variant="ghost" 
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            data-testid="image-upload-btn"
          >
            <Image className="w-5 h-5" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />

          <Input
            placeholder={t('type_message') || 'Type a message...'}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            className="flex-1"
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            disabled={(!chatInput.trim() && !imagePreview) || sendingMessage}
            data-testid="send-message-btn"
          >
            {sendingMessage ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              onClick={onOpenBroadcast}
              className="gap-2"
              data-testid="broadcast-btn"
            >
              <Bell className="w-4 h-4" />
              {t('broadcast')}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChatTab;
